/*
 * Inventory Phase 3 (r16; NecroGrowth docs/01-inventory/01.5-phase2-3.md): the pure logic behind the Problems,
 * Capacity and Assign / move views - query params, the assign request body, how a 409 "needs confirm" answer is read,
 * severity order and labels. No React and no network here, so every rule is unit-tested (inventoryOps.test.js).
 *
 * The SERVER scopes everything (a client sees only its own problems and phones; only the admin assigns); these helpers
 * never decide access.
 */

export const SEVERITIES = ['high', 'medium', 'low'];

export const SEVERITY_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };

/** Badge classes per severity (the app's danger / warning / muted tokens). */
export function severityClass(severity) {
  switch (severity) {
    case 'high': return 'border-danger/30 bg-danger/10 text-danger';
    case 'medium': return 'border-warning/30 bg-warning/10 text-warning';
    default: return 'border-border bg-secondary text-muted-foreground';
  }
}

/** The /problems query params from the view's filters (empty values left out). */
export function problemParams({ type, severity, clientId } = {}) {
  const p = {};
  if (type) p.type = type;
  if (severity) p.severity = severity;
  if (clientId) p.clientId = clientId;
  return p;
}

/** Client-side search over the loaded problems (title, detail, label). */
export function searchProblems(problems, q) {
  const needle = (q || '').trim().toLowerCase();
  if (!needle) return problems || [];
  return (problems || []).filter((p) => [p.title, p.detail, p.label, p.type].some((s) => String(s || '').toLowerCase().includes(needle)));
}

/** The type summary the board header shows: types with at least one problem, high severity first, then by count. */
export function typeSummary(board) {
  const types = (board?.types || []).filter((t) => Number(t.count) > 0);
  return [...types].sort((a, b) => SEVERITIES.indexOf(a.severity) - SEVERITIES.indexOf(b.severity) || Number(b.count) - Number(a.count));
}

/**
 * The /capacity query params: platform (default INSTAGRAM), accounts (a whole number >= 1), clientId (admin), accountId
 * (admin: count for THAT account - re-review should-fix 1: a no-client account counts as itself, not as a new client).
 */
export function capacityParams({ platform, accounts, clientId, accountId } = {}) {
  const p = { platform: platform || 'INSTAGRAM' };
  const n = Number(accounts);
  if (accounts !== '' && accounts != null && Number.isInteger(n) && n >= 1) p.accounts = n;
  if (clientId) p.clientId = clientId;
  if (accountId) p.accountId = accountId;
  return p;
}

export const TIER_LABEL = {
  1: 'ready now',
  2: 'after installing the app',
  3: 'on a spare phone',
  4: 'phone missing / offline',
};

/**
 * The POST /assign body from the dialog's form.
 * mode: 'auto' | 'pick' | 'release' | 'slotOnly'; slotMode: 'keep' | 'set' | 'unlink'.
 */
export function assignBody(accountId, form, { dryRun = false, confirm = false } = {}) {
  const b = { accountId };
  if (form.mode === 'auto') {
    b.auto = true;
    if (form.phoneId) b.phoneId = form.phoneId;
  } else if (form.mode === 'pick') {
    b.profileId = form.profileId;
  } else if (form.mode === 'release') {
    b.release = true;
  }
  if (form.slotMode === 'set' && form.slotId) b.slotId = form.slotId;
  if (form.slotMode === 'unlink') b.slotId = null;
  // Re-review must-fix 1: a shared phone is never claimed - the server refuses claimPhone on one, so the dialog never
  // sends both (ticking "allow shared" unticks the claim).
  if (form.claimPhone && !form.allowSharedPhone) b.claimPhone = true;
  if (form.allowSharedPhone) b.allowSharedPhone = true;
  if (form.note && form.note.trim()) b.note = form.note.trim();
  if (dryRun) b.dryRun = true;
  if (confirm) b.confirmLiveSlot = true;
  return b;
}

/** Why the form cannot be sent yet, or null. */
export function assignProblem(form) {
  if (form.mode === 'pick' && !form.profileId) return 'Pick a free profile first.';
  if (form.mode === 'slotOnly' && form.slotMode === 'keep') return 'Choose a slot change (link or unlink).';
  if (form.slotMode === 'set' && !form.slotId) return 'Pick the slot to link.';
  return null;
}

/** A 409 answer that asks for confirmLiveSlot: its body (plan + liveSlots), else null. */
export function needsConfirm(error) {
  const r = error?.response;
  if (r?.status === 409 && r.data && r.data.needsConfirm) return r.data;
  return null;
}

/** "slot3 (#24): slot slot3 is active and not paused ...; ..." per live slot, for a confirm prompt. */
export function liveSlotLines(liveSlots) {
  return (liveSlots || []).map((s) => `${s?.slot?.label || 'slot'}: ${(s?.reasons || []).join('; ')}`);
}

/** The free profiles a capacity answer offers as picker options: [{id, label, tier}]. */
export function planOptions(capacity) {
  return (capacity?.answer?.plan || []).map((p) => ({
    id: p.profileId,
    label: `user ${p.androidUserId} on ${p.phone?.label || 'phone'}`,
    tier: p.tier,
    tierLabel: TIER_LABEL[p.tier] || '',
  }));
}

/**
 * Phase 3 review must-fix 2: a phone Capacity lists but does not count (Assign would refuse it), as a short tag for the
 * per-phone table; the server's full reason (whyNot) goes in its tooltip. Null for a phone that counts.
 */
export function notCountedTag(phone) {
  if (!phone || phone.eligible !== false) return null;
  const why = String(phone.whyNot || '');
  if (why.includes("customer's PC")) return "on a customer's PC";
  if (why.includes("already holds another client's account")) return "shared: another client's account";
  if (why.includes('belongs to another client')) return "another client's";
  return 'not counted';
}

/**
 * Re-review should-fix 5: a grid edit, create, bulk edit or CSV import that leaves a placement Assign would refuse (an
 * account on another client's phone, a shared phone given a client) is still saved - it is the admin's own edit - and the
 * server answers {warnings}. One toast text for them (the first three, then a count), or null when there are none.
 */
export function placementWarningText(data) {
  const w = (data?.warnings || []).filter((x) => typeof x === 'string' && x.trim());
  if (!w.length) return null;
  return w.length <= 3 ? w.join(' ') : `${w.slice(0, 3).join(' ')} (+${w.length - 3} more)`;
}

/** Phase 3 review must-fix 4: "Scan now" while a scan runs answers 409 "already running" - news, not an error. */
export function scanAlreadyRunning(error) {
  const r = error?.response;
  return r?.status === 409 && /already running/i.test(String(r?.data?.error || ''));
}

/** An assignment row as one line. */
export function opLine(op) {
  const kind = op?.kind === 'undo' ? `undo of #${op.undoOf}` : op?.kind;
  return `#${op?.id} ${kind}: ${op?.summary || ''}`.trim();
}

/**
 * INV-7b browser drive: the alert episodes (GET /alerts, admin) as rows for the Problems board's "Alert tickets" panel -
 * the API existed but nothing in V3 showed which problems went out as support tickets. Open first, then
 * resolved; by severity (high first), then newest first. state: 'open' | 'reopened' | 'resolved'; ticket: the NM- id or null while it waits for the per-scan cap.
 */
export function alertRows(data) {
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const out = rows.map((a) => {
    const reopened = Number(a?.reopened) || 0;
    const state = a?.resolvedAt ? 'resolved' : reopened > 0 ? 'reopened' : 'open';
    return {
      id: a?.id,
      severity: a?.severity || 'low',
      title: a?.title || a?.key || '',
      ticket: a?.ticketId || null,
      client: a?.client || null,
      firstSeenAt: a?.firstSeenAt || null,
      lastSeenAt: a?.lastSeenAt || null,
      resolvedAt: a?.resolvedAt || null,
      reopened,
      state,
    };
  });
  const rank = (r) => (r.state === 'resolved' ? 1 : 0);
  const sev = (r) => { const i = SEVERITIES.indexOf(r.severity); return i < 0 ? SEVERITIES.length : i; };
  return out.sort((x, y) => rank(x) - rank(y) || sev(x) - sev(y)
    || String(y.lastSeenAt || '').localeCompare(String(x.lastSeenAt || '')) || (y.id || 0) - (x.id || 0));
}

/** "2026-10-04T07:22:31.123" -> "2026-10-04 07:22" (server time as the server wrote it). */
export function shortTime(t) {
  if (!t) return '-';
  const s = String(t).replace('T', ' ');
  return s.length >= 16 ? s.slice(0, 16) : s;
}
